import { logger } from '@/utils/logger/logger';
import { isChunkLoadError } from '@/utils/retry-lazy';
import { isChunkErrorSimulated } from './settingsModalDevtools';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Wraps a dynamic-import callback with bounded exponential-backoff retries
 * for transient chunk-fetch failures. Used as the inner of every entry in
 * {@link screenImporters} so both `React.lazy` and `preloadSettingsScreen`
 * inherit the retry behaviour from a single point of definition.
 *
 * **Stale-hash failures (post-deploy) will still bubble out** — the user's
 * cached `main.js` references a hash that no longer exists on the CDN, so
 * every retry resolves to the same 404. Those land in
 * `SettingsModalScreenErrorBoundary` which offers a page-refresh CTA to
 * pull fresh hashes. Retries here exist to soak up the *truly* transient
 * cases (flaky network, intermittent edge errors) where one more attempt
 * a few hundred ms later succeeds.
 *
 * Why this lives next to `settingsScreenLoaders` rather than as a generic
 * util: the only consumers today are the modal's lazy section importers,
 * and the constants (`maxAttempts`, `backoffMs`) are tuned for the
 * "modal opens to a stale chunk" UX — short total wall-clock so the user
 * sees the error UI quickly instead of staring at an empty modal.
 */
export const settingsImportWithRetry = <T>(
  importer: () => Promise<T>,
  { maxAttempts = 3, backoffMs = 250 }: { maxAttempts?: number; backoffMs?: number } = {}
): Promise<T> => {
  // Dev-only chunk-error simulation. In production `isChunkErrorSimulated`
  // is a constant `false`, so the conditional folds away and `wrappedImporter`
  // is a direct reference to the caller's importer — zero overhead. See
  // `settingsModalDevtools.ts` for the toggle interface.
  const wrappedImporter: () => Promise<T> = isChunkErrorSimulated()
    ? () => {
        const err = new Error('Loading chunk 42 failed.');
        err.name = 'ChunkLoadError';
        return Promise.reject(err);
      }
    : importer;

  const attempt = async (remaining: number, delay: number): Promise<T> => {
    try {
      return await wrappedImporter();
    } catch (err) {
      if (remaining <= 1 || !isChunkLoadError(err)) throw err;
      logger.warn('[settings-modal] chunk import failed — retrying', {
        remainingAttempts: remaining - 1,
        delayMs: delay,
        error: err instanceof Error ? err.message : String(err),
      });
      await sleep(delay);
      return attempt(remaining - 1, delay * 2);
    }
  };
  return attempt(maxAttempts, backoffMs);
};
